import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';

const SavedRitualsContext = createContext(null);
const STORAGE_KEY = 'hwl_saved_rituals_v1';
const REMINDER_KEY = 'hwl_ritual_reminders_v1';
const FIRED_KEY = 'hwl_reminders_fired_v1';
const NOTES_KEY = 'hwl_ritual_notes_v1';
const COMPLETIONS_KEY = 'hwl_ritual_completions_v1';

// Helpers for date math (local time)
const dateKey = (d = new Date()) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const parseKey = (s) => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const daysBetween = (a, b) => Math.round((b - a) / (24 * 60 * 60 * 1000));

// reminders: { recipeId: { time: 'HH:MM', label } }
// fired: { 'YYYY-MM-DD-recipeId': true }

export const SavedRitualsProvider = ({ children }) => {
  const [savedIds, setSavedIds] = useState([]);
  const [reminders, setReminders] = useState({});
  const [fired, setFired] = useState({});
  const [notes, setNotes] = useState({});
  const [completions, setCompletions] = useState({}); // { recipeId: [dateKey, ...] }
  const [notificationPermission, setNotificationPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'denied'
  );

  // Load from storage on mount
  useEffect(() => {
    try {
      const s = localStorage.getItem(STORAGE_KEY);
      if (s) setSavedIds(JSON.parse(s));
      const r = localStorage.getItem(REMINDER_KEY);
      if (r) setReminders(JSON.parse(r));
      const f = localStorage.getItem(FIRED_KEY);
      if (f) setFired(JSON.parse(f));
      const n = localStorage.getItem(NOTES_KEY);
      if (n) setNotes(JSON.parse(n));
      const c = localStorage.getItem(COMPLETIONS_KEY);
      if (c) setCompletions(JSON.parse(c));
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { localStorage.setItem(STORAGE_KEY, JSON.stringify(savedIds)); }, [savedIds]);
  useEffect(() => { localStorage.setItem(REMINDER_KEY, JSON.stringify(reminders)); }, [reminders]);
  useEffect(() => { localStorage.setItem(FIRED_KEY, JSON.stringify(fired)); }, [fired]);
  useEffect(() => { localStorage.setItem(NOTES_KEY, JSON.stringify(notes)); }, [notes]);
  useEffect(() => { localStorage.setItem(COMPLETIONS_KEY, JSON.stringify(completions)); }, [completions]);

  const isSaved = useCallback((id) => savedIds.includes(id), [savedIds]);
  const toggleSaved = useCallback((id) => {
    setSavedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }, []);
  const removeSaved = useCallback((id) => setSavedIds(prev => prev.filter(x => x !== id)), []);
  const clearSaved = useCallback(() => setSavedIds([]), []);

  const setReminder = useCallback((recipeId, time, label) => {
    setReminders(prev => ({ ...prev, [recipeId]: { time, label } }));
  }, []);
  const removeReminder = useCallback((recipeId) => {
    setReminders(prev => {
      const n = { ...prev };
      delete n[recipeId];
      return n;
    });
  }, []);

  const setNote = useCallback((recipeId, text) => {
    setNotes(prev => {
      if (!text || !text.trim()) {
        const n = { ...prev };
        delete n[recipeId];
        return n;
      }
      return { ...prev, [recipeId]: text };
    });
  }, []);

  const getNote = useCallback((recipeId) => notes[recipeId] || '', [notes]);

  // ---- Completions / Streak ----
  const isDoneToday = useCallback((recipeId) => {
    const list = completions[recipeId] || [];
    return list.includes(dateKey());
  }, [completions]);

  const markDone = useCallback((recipeId, recipeTitle) => {
    setCompletions(prev => {
      const today = dateKey();
      const existing = prev[recipeId] || [];
      if (existing.includes(today)) return prev;
      const next = { ...prev, [recipeId]: [...existing, today].sort() };
      // Fire milestone event using the NEW completions data
      try {
        const set = new Set(next[recipeId]);
        const t = new Date();
        let cursor = set.has(dateKey(t)) ? t : new Date(t.getTime() - 86400000);
        let streak = 0;
        while (set.has(dateKey(cursor))) {
          streak += 1;
          cursor = new Date(cursor.getTime() - 86400000);
        }
        if ([3, 7, 14, 30, 60, 100].includes(streak)) {
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('hwl:streak-milestone', {
              detail: { streak, recipeId, recipeTitle }
            }));
          }, 300);
        }
      } catch (e) { console.warn('Streak milestone dispatch failed', e); }
      return next;
    });
  }, []);

  const unmarkDone = useCallback((recipeId) => {
    setCompletions(prev => {
      const today = dateKey();
      const existing = prev[recipeId] || [];
      return { ...prev, [recipeId]: existing.filter(d => d !== today) };
    });
  }, []);

  // Streak = number of consecutive days ending today (or yesterday if today not done)
  const getStreak = useCallback((recipeId) => {
    const list = completions[recipeId] || [];
    if (list.length === 0) return 0;
    const set = new Set(list);
    const today = new Date();
    // If today isn't done, start counting from yesterday (streak can survive today until midnight)
    let cursor = set.has(dateKey(today)) ? today : new Date(today.getTime() - 86400000);
    if (!set.has(dateKey(cursor))) return 0;
    let count = 0;
    while (set.has(dateKey(cursor))) {
      count += 1;
      cursor = new Date(cursor.getTime() - 86400000);
    }
    return count;
  }, [completions]);

  const getTotalCompletions = useCallback((recipeId) => (completions[recipeId] || []).length, [completions]);

  // Weekly stats = last 7 days
  const getWeeklyStats = useCallback(() => {
    const today = new Date();
    const cutoff = new Date(today.getTime() - 6 * 86400000); // includes today \u2192 7 days
    const stats = { totalCompletions: 0, byRitual: {}, activeDays: new Set() };
    Object.entries(completions).forEach(([recipeId, dates]) => {
      dates.forEach(d => {
        const dd = parseKey(d);
        if (dd >= cutoff && dd <= today) {
          stats.totalCompletions += 1;
          stats.byRitual[recipeId] = (stats.byRitual[recipeId] || 0) + 1;
          stats.activeDays.add(d);
        }
      });
    });
    stats.activeDayCount = stats.activeDays.size;
    return stats;
  }, [completions]);

  const requestNotificationPermission = useCallback(async () => {
    if (typeof Notification === 'undefined') return 'denied';
    if (Notification.permission === 'granted') return 'granted';
    const result = await Notification.requestPermission();
    setNotificationPermission(result);
    return result;
  }, []);

  const todayKey = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const markFired = useCallback((recipeId) => {
    const key = `${todayKey()}-${recipeId}`;
    setFired(prev => ({ ...prev, [key]: true }));
  }, []);

  const hasFiredToday = useCallback((recipeId) => {
    return !!fired[`${todayKey()}-${recipeId}`];
  }, [fired]);

  // Poll every minute for due reminders (only when tab is open).
  useEffect(() => {
    const check = () => {
      const now = new Date();
      const currentHM = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
      Object.entries(reminders).forEach(([recipeId, { time, label }]) => {
        // Fire when current time matches (or is within 3 min after) the scheduled time
        if (!time) return;
        const [h, m] = time.split(':').map(Number);
        const scheduled = h * 60 + m;
        const current = now.getHours() * 60 + now.getMinutes();
        const delta = current - scheduled;
        if (delta >= 0 && delta <= 3 && !hasFiredToday(recipeId)) {
          markFired(recipeId);
          window.dispatchEvent(new CustomEvent('hwl:ritual-due', { detail: { recipeId, label } }));
          if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
            try {
              new Notification('Time for your ritual \u{1F33F}', {
                body: label ? `\u201C${label}\u201D is waiting for you.` : 'A moment of care is waiting for you.',
                icon: '/favicon.ico',
              });
            } catch (e) { console.warn('Notification API failed', e); }
          }
        }
      });
    };
    check();
    const int = setInterval(check, 60 * 1000);
    return () => clearInterval(int);
  }, [reminders, hasFiredToday, markFired]);

  const value = useMemo(() => ({
    savedIds,
    savedCount: savedIds.length,
    isSaved,
    toggleSaved,
    removeSaved,
    clearSaved,
    reminders,
    setReminder,
    removeReminder,
    notes,
    setNote,
    getNote,
    completions,
    isDoneToday,
    markDone,
    unmarkDone,
    getStreak,
    getTotalCompletions,
    getWeeklyStats,
    notificationPermission,
    requestNotificationPermission,
  }), [savedIds, isSaved, toggleSaved, removeSaved, clearSaved, reminders, setReminder, removeReminder, notes, setNote, getNote, completions, isDoneToday, markDone, unmarkDone, getStreak, getTotalCompletions, getWeeklyStats, notificationPermission, requestNotificationPermission]);

  return <SavedRitualsContext.Provider value={value}>{children}</SavedRitualsContext.Provider>;
};

export const useSavedRituals = () => {
  const ctx = useContext(SavedRitualsContext);
  if (!ctx) throw new Error('useSavedRituals must be used within SavedRitualsProvider');
  return ctx;
};
